/**
 * Diary Search — port of mobile services/diarySearch.ts.
 *
 * Data layer for "search my logged diary entries": pulls two windows of the
 * user's own entries (food, and food with liquid volume — 4.41 S3), collapses
 * repeat logs of the same food into one group, and filters those groups by a
 * normalized text query.
 *
 * Everything except the module-level cache is pure, so the grouping/ranking
 * rules can be unit-tested without Firestore.
 */

import { DiaryService } from '@/lib/firestore/diary'
import type { DiaryEntry, MealTime } from '@/types'

/**
 * How many raw entries make up the FOOD window (`isFluid == false`). At ~5
 * entries/day this is roughly 100 days of history; the long tail is repeats,
 * not new foods.
 */
const FOOD_WINDOW_LIMIT = 500

/**
 * How many raw entries make up the LIQUID window (`isFluid == true`, 4.41 S3):
 * soups, sauces and drinks logged as food, plus Fluid Log rows that readers
 * drop by `source`. Its own cap keeps heavy water tracking from pushing food
 * out of the food window, on the same index.
 */
const LIQUID_WINDOW_LIMIT = 300

type WindowKind = 'food' | 'liquid'

const WINDOW_SPECS: Record<WindowKind, { isFluid: boolean; limit: number }> = {
  food: { isFluid: false, limit: FOOD_WINDOW_LIMIT },
  liquid: { isFluid: true, limit: LIQUID_WINDOW_LIMIT },
}

/** One time a food was logged. */
export interface DiarySearchOccurrence {
  entryId: string
  date: string // 'YYYY-MM-DD'
  mealType: MealTime
}

/** All logs of one food, collapsed into a single row. */
export interface FoodSearchGroup {
  key: string // normalized name — stable React key
  displayName: string // name from the most recent entry
  count: number
  lastLoggedDate: string // 'YYYY-MM-DD'
  mostRecentEntry: DiaryEntry // nutrients source AND the re-log payload
  occurrences: DiarySearchOccurrence[] // date-desc
}

/**
 * A group plus its search index. `searchText` is an index, not something the
 * UI renders; it rides along on the objects getSearchGroups returns and
 * filterGroups reads it back through haystackFor(). A group's `key` IS its
 * normalized name, so there is no separate name haystack.
 */
type IndexedGroup = FoodSearchGroup & { searchText: string }

export interface DiarySearchDataSource {
  fetchWindow(
    userId: string,
    opts: { limit: number; isFluid: boolean; createdAfter?: Date },
  ): Promise<DiaryEntry[]>
}

const firestoreWindowDataSource: DiarySearchDataSource = {
  fetchWindow: (userId, opts) => DiaryService.getRecentRawEntries(userId, opts),
}

const dataSource: DiarySearchDataSource = firestoreWindowDataSource

/**
 * Lowercase, strip diacritics, trim — "nusse" has to find "Nüsse".
 */
export function normalizeSearchText(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // combining diacritical marks
    .trim()
}

/**
 * SEARCH WINDOW CACHE — module-level singleton so reopening search is
 * instant; topped up incrementally rather than refetched.
 *
 * Two windows (4.41 S3), one per query, each with its OWN entries, cursor,
 * `loaded` flag and cap — they never share a cursor or a trim, so new water
 * rows can't trim food out. Readers see the union (`mergeWindows`).
 *
 * Deliberately NO eager insert on new logs: writes carry a pending
 * serverTimestamp() that would poison `newestCreatedAt`. The incremental
 * fetch picks them up next time search is opened.
 */
interface SearchWindow {
  entries: Map<string, DiaryEntry>
  newestCreatedAt: Date | null
  /** True once a fetch for this window has succeeded; until then it loads cold. */
  loaded: boolean
}

interface SearchCache {
  userId: string | null
  food: SearchWindow
  liquid: SearchWindow
}

function emptyWindow(): SearchWindow {
  return { entries: new Map<string, DiaryEntry>(), newestCreatedAt: null, loaded: false }
}

const cache: SearchCache = {
  userId: null,
  food: emptyWindow(),
  liquid: emptyWindow(),
}

/**
 * Union of both windows by entry id — an entry in both (its `isFluid` changed
 * between fetches) never counts twice. The food window's copy wins.
 */
function mergeWindows(windows: Pick<SearchCache, 'food' | 'liquid'>): DiaryEntry[] {
  const merged = new Map<string, DiaryEntry>(windows.food.entries)
  for (const [id, entry] of windows.liquid.entries) {
    if (!merged.has(id)) merged.set(id, entry)
  }
  return [...merged.values()]
}

/** createdAt is briefly absent on unacknowledged writes — fall back to timestamp. */
function entryTimeMs(entry: DiaryEntry): number {
  const stamp: Date | undefined = entry.createdAt ?? entry.timestamp
  return stamp ? stamp.getTime() : 0
}

/**
 * Fluid Log rows (`source: 'fluid'`) are tracked separately, HealthKit entries
 * aren't ours to re-log. A food with liquid volume (`isFluid: true` — soup,
 * sauce, a drink logged as food) is searchable (4.41 S3).
 */
function isSearchable(entry: DiaryEntry): boolean {
  return (
    entry.source !== 'fluid' &&
    entry.source !== 'healthkit' &&
    typeof entry.name === 'string' &&
    entry.name.trim().length > 0
  )
}

/** Every normalized string an entry should be findable by: name + each food. */
function collectHaystackTerms(entry: DiaryEntry, into: Set<string>): void {
  const name = normalizeSearchText(entry.name ?? '')
  if (name) into.add(name)
  for (const food of entry.foods ?? []) {
    const foodName = normalizeSearchText(food?.name ?? '')
    if (foodName) into.add(foodName)
  }
}

/** Newest logged date first; ties keep their incoming (createdAt-desc) order. */
function byLastLoggedDesc<T extends { lastLoggedDate: string }>(groups: T[]): T[] {
  return [...groups].sort((a, b) => b.lastLoggedDate.localeCompare(a.lastLoggedDate))
}

/** Collapse raw entries into one group per distinct (normalized) food name. */
function buildGroups(entries: DiaryEntry[]): IndexedGroup[] {
  const eligible = entries.filter(isSearchable).sort((a, b) => entryTimeMs(b) - entryTimeMs(a))

  const terms = new Map<string, Set<string>>()
  const groups = new Map<string, FoodSearchGroup>()

  for (const entry of eligible) {
    const key = normalizeSearchText(entry.name)
    if (!key) continue

    let group = groups.get(key)
    if (!group) {
      // Entries arrive newest-first, so the first one seen for a key is the
      // most recent: it owns the display name, nutrients, and re-log payload.
      group = {
        key,
        displayName: entry.name.trim(),
        count: 0,
        lastLoggedDate: entry.date,
        mostRecentEntry: entry,
        occurrences: [],
      }
      groups.set(key, group)
      terms.set(key, new Set<string>())
    }

    group.count += 1
    group.occurrences.push({ entryId: entry.id, date: entry.date, mealType: entry.mealType })
    collectHaystackTerms(entry, terms.get(key)!)
  }

  const indexed: IndexedGroup[] = []
  for (const group of groups.values()) {
    group.occurrences.sort((a, b) => b.date.localeCompare(a.date))
    // A day-copy writes a brand-new entry onto an OLDER date, so "created
    // last" and "eaten last" differ — read the date off sorted occurrences.
    group.lastLoggedDate = group.occurrences[0]?.date ?? group.lastLoggedDate
    indexed.push({ ...group, searchText: [...(terms.get(group.key) ?? [])].join(' ') })
  }

  return byLastLoggedDesc(indexed)
}

/**
 * Merge one successful fetch into ITS window: by id (a boundary timestamp that
 * comes back twice updates in place instead of double-counting), then the
 * cursor, then a trim to that window's cap, newest first. Never touches the
 * other window.
 */
function commitWindow(w: SearchWindow, fetched: DiaryEntry[], cap: number): void {
  let newest = w.newestCreatedAt
  for (const entry of fetched) {
    w.entries.set(entry.id, entry)
    if (entry.createdAt && (!newest || entry.createdAt > newest)) {
      newest = entry.createdAt
    }
  }
  w.newestCreatedAt = newest
  w.loaded = true

  // Keep the working set bounded across repeated top-ups.
  if (w.entries.size > cap) {
    const trimmed = [...w.entries.values()]
      .sort((a, b) => entryTimeMs(b) - entryTimeMs(a))
      .slice(0, cap)
    w.entries = new Map(trimmed.map((entry) => [entry.id, entry]))
  }
}

/**
 * Load (or top up) both windows and return the RAW cached entries (latest
 * FOOD_WINDOW_LIMIT food + LIQUID_WINDOW_LIMIT liquid entries by creation
 * time). Shared by search (groups by name) and "Most used" (counts by
 * saved-food id). Each window fetches from its own cursor (none until it has
 * loaded) and commits only its own result. A failed food fetch rejects (a
 * successful liquid result is still kept); a failed liquid fetch leaves that
 * window as it was and the call returns food + the old liquid entries.
 */
async function topUpWindow(userId: string): Promise<DiaryEntry[]> {
  if (cache.userId !== userId) {
    // Never serve one account's history to another.
    cache.userId = userId
    cache.food = emptyWindow()
    cache.liquid = emptyWindow()
  }

  // Hold THIS call's windows: a sign-out mid-fetch swaps in fresh ones, so a
  // late result lands in a detached window, never in the next account's cache.
  const windows = { food: cache.food, liquid: cache.liquid }

  const fetchInto = async (kind: WindowKind): Promise<DiaryEntry[]> => {
    const w = windows[kind]
    const cursor = w.loaded ? w.newestCreatedAt : null
    return dataSource.fetchWindow(userId, {
      limit: WINDOW_SPECS[kind].limit,
      isFluid: WINDOW_SPECS[kind].isFluid,
      ...(cursor ? { createdAfter: cursor } : {}),
    })
  }

  const [food, liquid] = await Promise.allSettled([fetchInto('food'), fetchInto('liquid')])

  if (liquid.status === 'fulfilled') {
    commitWindow(windows.liquid, liquid.value, WINDOW_SPECS.liquid.limit)
  }
  if (food.status === 'rejected') throw food.reason
  commitWindow(windows.food, food.value, WINDOW_SPECS.food.limit)

  return mergeWindows(windows)
}

/** Load (or top up) the search window and return it grouped, most recent first. */
export async function getSearchGroups(userId: string): Promise<FoodSearchGroup[]> {
  return buildGroups(await topUpWindow(userId))
}

/**
 * MOST USED SAVED FOODS (4.37 F4) — port of mobile services/diarySearch.ts.
 * Counted from the diary window (`source === 'favorite' && sourceId === id`),
 * never from a stored counter: no writes, no double counting, history counts
 * from day one. Recent logs only, hence "Most used (recent logs)". A combined
 * favorite counts only its own id (merges don't record their source ids).
 */
export interface FavoriteUsage {
  count: number
  /** Creation time of the newest counted entry (ms) — tie-break fallback. */
  lastLoggedMs: number
}

/**
 * Aggregate RAW entries by saved-food id — never search groups, which are keyed
 * by name, drop `source` / `sourceId`, and can mix several saved foods. A saved
 * drink or soup logged as food (`isFluid: true`) counts (4.41 S3); Fluid Log
 * rows are never `source: 'favorite'`.
 */
export function countFavoriteUses(entries: DiaryEntry[]): Map<string, FavoriteUsage> {
  const usage = new Map<string, FavoriteUsage>()
  for (const entry of entries) {
    if (entry.source !== 'favorite') continue
    const id = entry.sourceId
    if (typeof id !== 'string' || id.length === 0) continue
    const time = entryTimeMs(entry)
    const prev = usage.get(id)
    if (prev) {
      prev.count += 1
      if (time > prev.lastLoggedMs) prev.lastLoggedMs = time
    } else {
      usage.set(id, { count: 1, lastLoggedMs: time })
    }
  }
  return usage
}

/**
 * Top up the window (cold or warm — no eager inserts) and count saved-food
 * uses. Rejects when the window can't be loaded; callers fall back to
 * "Recently used". Web writes are awaited to the server ack by addEntry, so
 * a refresh after an awaited add already sees the new entry.
 */
export async function getFavoriteUsage(userId: string): Promise<Map<string, FavoriteUsage>> {
  return countFavoriteUses(await topUpWindow(userId))
}

/** Date / Firestore Timestamp / millis → ms; anything else → 0. */
function toMs(value: unknown): number {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  if (value && typeof value === 'object') {
    const v = value as { toMillis?: () => number; toDate?: () => Date }
    if (typeof v.toMillis === 'function') return v.toMillis()
    if (typeof v.toDate === 'function') return v.toDate().getTime()
  }
  return 0
}

/**
 * "Most used" order: count ↓, then last used ↓ (`lastUsedAt`, falling back to
 * the newest log in the window), then name. An empty usage map degrades to
 * "Recently used".
 */
export function sortByMostUsed<T extends { id: string; name: string; lastUsedAt?: unknown }>(
  foods: T[],
  usage: Map<string, FavoriteUsage>,
): T[] {
  const lastUsed = (f: T) => toMs(f.lastUsedAt) || usage.get(f.id)?.lastLoggedMs || 0
  return [...foods].sort((a, b) => {
    const byCount = (usage.get(b.id)?.count ?? 0) - (usage.get(a.id)?.count ?? 0)
    if (byCount !== 0) return byCount
    const byUse = lastUsed(b) - lastUsed(a)
    if (byUse !== 0) return byUse
    return (a.name ?? '').localeCompare(b.name ?? '')
  })
}

function haystackFor(group: FoodSearchGroup): string {
  const indexed = group as Partial<IndexedGroup>
  if (typeof indexed.searchText === 'string') return indexed.searchText
  const terms = new Set<string>([group.key])
  if (group.mostRecentEntry) collectHaystackTerms(group.mostRecentEntry, terms)
  return [...terms].join(' ')
}

/**
 * Filter groups by a free-text query. Empty query returns the groups
 * untouched (recency browse). Name matches rank above foods-only matches.
 */
export function filterGroups(groups: FoodSearchGroup[], query: string): FoodSearchGroup[] {
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return groups

  const nameMatches: FoodSearchGroup[] = []
  const foodMatches: FoodSearchGroup[] = []

  for (const group of groups) {
    const haystack = haystackFor(group)
    // AND across tokens, substring within one.
    if (!tokens.every((token) => haystack.includes(token))) continue
    if (tokens.every((token) => group.key.includes(token))) nameMatches.push(group)
    else foodMatches.push(group)
  }

  return [...byLastLoggedDesc(nameMatches), ...byLastLoggedDesc(foodMatches)]
}

/** Drop a deleted entry (from both windows) so it can't come back as a phantom result. */
export function diarySearchCacheRemove(entryId: string): void {
  cache.food.entries.delete(entryId)
  cache.liquid.entries.delete(entryId)
}

/** Apply an edit to the cached copy in whichever window holds it (updates leave createdAt alone, so the incremental fetch never sees them). */
export function diarySearchCachePatch(entryId: string, updates: Partial<DiaryEntry>): void {
  for (const w of [cache.food, cache.liquid]) {
    const existing = w.entries.get(entryId)
    if (!existing) continue
    w.entries.set(entryId, { ...existing, ...updates, id: existing.id })
  }
}

/** Wipe everything (both windows) on sign-out. */
export function diarySearchCacheClear(): void {
  cache.userId = null
  cache.food = emptyWindow()
  cache.liquid = emptyWindow()
}
